import type { TipTapDocument, TipTapVariable } from '@poseidon/components/Input2/TipTapInput';

import { updateWithToast } from '@/clientOnlyUtils/clientUtils';
import { api } from '@/utils/api';
import { ContactSequenceGraphCard } from '~/components/Sequence/ContactSequenceGraphCard';

interface Props {
  teamId: string;
  /** The open contact's public id. */
  contactId: string;
  /** `@`-variables + `/`-documents for the per-step content editor (this contact's values). */
  variables?: TipTapVariable[];
  documents?: TipTapDocument[];
}

/**
 * The contact detail page's per-contact sequence view: for every sequence this contact is actively enrolled
 * in, render the LOCKED touch-sequence graph (see {@link ContactSequenceGraphCard}) and route each step's
 * body/subject edit to a per-contact override on `flow.enrollment.drafts` — the template sequence is never
 * touched. Gated by the host on `userTeam.sequenceEnabled`; renders nothing when the contact is in no sequence.
 */
export function ContactSequenceGraph({ teamId, contactId, variables, documents }: Props) {
  const utils = api.useUtils();
  const graphsQuery = api.sequence.enrollmentGraphsForContact.useQuery(
    { teamId, contactId },
    { enabled: Boolean(contactId) },
  );
  const setStepContent = api.sequence.setEnrollmentStepContent.useMutation({
    onSuccess: () => void utils.sequence.enrollmentGraphsForContact.invalidate({ teamId, contactId }),
  });
  // Mark a parked call/reminder to-do done for THIS contact → advance the arm, then refetch so the step flips
  // to sent + the next one becomes current. (`sequence.completeTask` resolves the arm from the wait node id.)
  const completeTask = api.sequence.completeTask.useMutation({
    onSuccess: () => void utils.sequence.enrollmentGraphsForContact.invalidate({ teamId, contactId }),
  });

  const enrollments = graphsQuery.data?.enrollments ?? [];
  if (enrollments.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {enrollments.map((enrollment) => (
        <ContactSequenceGraphCard
          key={enrollment.enrollmentId}
          enrollment={enrollment}
          teamId={teamId}
          variables={variables}
          documents={documents}
          onSaveStepContent={async (overrideKey, next) => {
            await updateWithToast({
              loading: 'Saving message for this contact…',
              success: 'Saved for this contact',
              mutationFnAsync: () =>
                setStepContent.mutateAsync({
                  teamId,
                  contactId,
                  sequenceId: enrollment.sequencePublicId,
                  overrideKey,
                  body: next.body ?? '',
                  subject: next.subject,
                }),
            });
          }}
          onCompleteManualStep={async (nodeId, stepKey) => {
            await updateWithToast({
              loading: 'Marking done…',
              // Same honesty as the builder's pile: nothing matched → say so (see useSequenceFaceActions).
              success: (data) =>
                data != null && data.completed === 0
                  ? 'Nothing to mark done — that to-do already moved on'
                  : 'Marked done — sequence advanced',
              mutationFnAsync: () =>
                completeTask.mutateAsync({
                  teamId,
                  sequenceId: enrollment.sequencePublicId,
                  contactId,
                  nodeId,
                  stepKey,
                }),
            });
          }}
        />
      ))}
    </div>
  );
}
